import { LessonDetailsContent } from "content";
import { Heading } from "components/Typography/Heading";
import { Loader } from "components/Common/Loader";
import { Divider } from "components/Common/Divider";
import { Space } from "components/Common/Space";
import { Button } from "components/Interactive/Button";
import { LessonContentChapter } from "../LessonContentChapter";
import { HomeworkMoveModal } from "../HomeworkMoveModal";
import type { TCourseLesson, TCourseLessonChapter } from "types/api/course";
import "./LessonContent.styles.scss";
import { useReactQuerySubscription } from "hooks/api/web-sockets";
import { useParams, useSearchParams } from "react-router-dom";
import { useModal, useRole } from "hooks/common";
import { useIsClassroom } from "hooks/common/useIsClassroom/useIsClassroom";
import {
  useApplyMoveToAllStudents,
  useBatchMoveExercises,
} from "hooks/api/student-progress";
import {
  ExerciseSelectionProvider,
  useExerciseSelection,
} from "context/ExerciseSelection";

type LessonContentProps = {
  /**
   * Indicates active chapter item ID, if any.
   * @default null
   */
  activeChapterId?: null | number;
  /**
   * If `true` component will display loader with animation.
   * @default false
   */
  loading?: boolean;
  /**
   * Lesson data to display.
   */
  lesson?: TCourseLesson;
  /**
   * Lesson chapters data to display.
   */
  lessonChapters?: TCourseLessonChapter[];
  /**
   * Student ID for group context (when studentId is not in URL params)
   * @default undefined
   */
  overrideStudentId?: string;
  /**
   * Props for StudentSwitcher component (for groups)
   * @default undefined
   */
  studentSwitcherProps?: {
    studentIds: number[];
    selectedStudentId?: number;
    onStudentSelect: (studentId: number) => void;
    loading?: boolean;
  };
  /**
   * Definition ID of the exercise to deep-link to within the active chapter.
   * @default null
   */
  deepLinkExerciseId?: number | null;
  /**
   * Seconds into the video the deep-linked VIDEO exercise should start at.
   * @default null
   */
  deepLinkStartSeconds?: number | null;
};

const LessonContentInner: React.FC<LessonContentProps> = (props) => {
  const {
    activeChapterId = null,
    loading = false,
    lesson,
    lessonChapters = [],
    overrideStudentId,
    studentSwitcherProps,
    deepLinkExerciseId = null,
    deepLinkStartSeconds = null,
  } = props;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const chapterId = searchParams.get("chapterId");
  const { isUserTeacher } = useRole();
  const { isClassroom } = useIsClassroom();
  const { selectedIds, selectedExerciseIds, clearSelection } =
    useExerciseSelection();
  const { mutate: batchMove, isLoading: isBatchMoving } = useBatchMoveExercises(
    overrideStudentId || params.studentId,
    params.courseId,
    params.lessonId
  );
  const { applyToAll, isLoading: isApplyingToAll } = useApplyMoveToAllStudents(
    params.courseId,
    params.lessonId,
    lessonChapters
  );

  const {
    isModalOpen: isMoveModalOpen,
    openModal: openMoveModal,
    closeModal: closeMoveModal,
  } = useModal(false);

  const isMoving = isBatchMoving || isApplyingToAll;

  const addToHomeworkClickHandler = () => {
    if (studentSwitcherProps) {
      openMoveModal();
    } else {
      batchMove(
        { historyIds: selectedIds, homework: true },
        { onSuccess: clearSelection }
      );
    }
  };

  const handleMoveSingleStudent = () => {
    batchMove(
      { historyIds: selectedIds, homework: true },
      {
        onSuccess: () => {
          clearSelection();
          closeMoveModal();
        },
      }
    );
  };

  const handleMoveAllStudents = async () => {
    if (!studentSwitcherProps) return;
    try {
      await applyToAll(
        selectedExerciseIds,
        studentSwitcherProps.studentIds,
        true
      );
      clearSelection();
      closeMoveModal();
    } catch {
      // error already logged in hook
    }
  };

  useReactQuerySubscription({
    studentId: Number(overrideStudentId || params.studentId),
    courseId: Number(params.courseId),
    lessonId: Number(params.lessonId),
    chapterId: Number(chapterId),
  });

  return (
    <div className="nb-lesson-details-content-container">
      {loading ? (
        <Loader />
      ) : (
        <>
          <Space
            direction="horizontal"
            justify="between"
            align="center"
            fullWidth
          >
            <Heading>
              {lesson?.name || LessonDetailsContent.Content.Lesson.TITLE}
            </Heading>
            {isUserTeacher && isClassroom && selectedIds.length > 0 && (
              <Button
                variant="secondary"
                onClick={addToHomeworkClickHandler}
                disabled={isMoving}
              >
                Додати до домашньої роботи
              </Button>
            )}
          </Space>
          <Divider variant="dashed" margin="yAxis" marginSize="large" />
          {lessonChapters
            .filter((chapter) => chapter.id === activeChapterId)
            .map((chapter, index) => (
              <LessonContentChapter
                key={chapter.id}
                chapter={chapter}
                order={index + 1}
                active
                overrideStudentId={overrideStudentId}
                studentSwitcherProps={studentSwitcherProps}
                deepLinkExerciseId={deepLinkExerciseId}
                deepLinkStartSeconds={deepLinkStartSeconds}
              />
            ))}
        </>
      )}
      {studentSwitcherProps && (
        <HomeworkMoveModal
          open={isMoveModalOpen}
          text="Застосувати дію для:"
          onClose={closeMoveModal}
          onSingleStudent={handleMoveSingleStudent}
          onAllStudents={handleMoveAllStudents}
          loading={isMoving}
        />
      )}
    </div>
  );
};

export const LessonContent: React.FC<LessonContentProps> = (props) => (
  <ExerciseSelectionProvider>
    <LessonContentInner {...props} />
  </ExerciseSelectionProvider>
);
